import { useHyperliquidStore } from '@/stores/hyperliquidStore';
import type {
  WsBook,
  Trade,
  WsBbo,
  AllMids,
  Candle,
  WsActiveAssetCtx,
  WsOrder,
  WsUserEvent,
  WsUserFills,
  WsUserFundings,
  WsUserNonFundingLedgerUpdates,
  Notification,
  WebData2,
  OrderFilled,
} from '@/lib/hyperliquid/types';
import usePortfolioStore from '@/stores/portfolioStore';
import { useOrderStore, type RealtimeOrder } from '@/stores/orderStore';
import { useHyperliquid } from '@/hooks/useHyperliquid';
import { ActiveAssetData } from '@/api/hyperliquid';
import { Address } from 'viem';
import { NewBlock, useBlockStore } from '@/stores/blockStore';

interface SubscriptionParams {
  type: string;
  coin?: string;
  user?: string;
  interval?: string;
  nSigFigs?: number;
  mantissa?: number;
  aggregateByTime?: boolean;
}

class WebSocketManager {
  private static instance: WebSocketManager;
  private ws: WebSocket | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private subscriptions = new Map<string, SubscriptionParams>();
  private isConnecting = false;
  private connectionListeners = new Set<(isConnected: boolean) => void>();

  // Throttling for high-frequency data
  private orderQueue: RealtimeOrder[] = [];
  private blockQueue: NewBlock[] = [];
  private orderFillQueue: OrderFilled[] = [];
  private orderThrottleTimer: NodeJS.Timeout | null = null;
  private blockThrottleTimer: NodeJS.Timeout | null = null;
  private orderFillThrottleTimer: NodeJS.Timeout | null = null;
  private readonly THROTTLE_DELAY = 100; // 100ms throttling

  private constructor() {
    this.connect();
    // Initial data loading is handled by DataLayout
  }

  static getInstance(): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();
    }
    return WebSocketManager.instance;
  }

  // ============ Connection Management ============
  private async connect() {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    console.log(process.env.NEXT_PUBLIC_WS_URL);

    try {
      this.ws = new WebSocket(process.env.NEXT_PUBLIC_WS_URL);

      this.ws.onopen = () => {
        console.log('✅ WebSocket connected');
        this.isConnecting = false;
        this.notifyConnectionChange(true);
        this.startPing();
        this.resubscribeAll();

        // Initialize default subscriptions on first connect
        if (this.subscriptions.size === 0) {
          this.initializeDefaultSubscriptions();
        }
      };

      this.ws.onmessage = event => {
        try {
          const message = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('Message parsing failed:', error);
        }
      };

      this.ws.onerror = error => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
      };

      this.ws.onclose = () => {
        console.log('❌ WebSocket disconnected');
        this.isConnecting = false;
        this.notifyConnectionChange(false);
        this.stopPing();
        this.scheduleReconnect();
      };
    } catch (error) {
      console.error('WebSocket creation failed:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, 3000);
  }

  private startPing() {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ method: 'ping' }));
      }
    }, 15000);
  }

  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  // ============ Message Handlers ============
  private handleMessage(message: any) {
    if (!message.channel) return;

    switch (message.channel) {
      case 'allMids':
        this.handleAllMids(message.data);
        break;
      case 'l2Book':
        this.handleOrderBook(message.data);
        break;
      case 'order':
        this.handleOrder(message.data);
        break;
      case 'orderFilled':
        this.handleOrderFilled(message.data);
        break;
      // case 'trades':
      //   this.handleTrades(message.data);
      //   break;

      case 'newBlock':
        this.handleBlock(message.data);
        break;

      // case 'bbo':
      //   this.handleBbo(message.data);
      //   break;
      // case 'candle':
      //   this.handleCandles(message.data);
      //   break;
      // case 'activeAssetCtx':
      //   this.handleAssetCtx(message.data);
      //   break;
      // case 'activeAssetData':
      //   this.handleAssetData(message.data);
      //   break;
      // case 'orderUpdates':
      //   this.handleOrderUpdates(message.data);
      //   break;
      // case 'userEvents':
      //   this.handleUserEvents(message.data);
      //   break;
      // case 'userFills':
      //   this.handleUserFills(message.data);
      //   break;
      // case 'userFundings':
      //   this.handleUserFundings(message.data);
      //   break;
      // case 'userNonFundingLedgerUpdates':
      //   this.handleLedgerUpdates(message.data);
      //   break;
      // case 'notification':
      //   this.handleNotification(message.data);
      //   break;
      // case 'webData2':
      //   this.handleWebData2(message.data);
      //   break;
      // case 'pong':
      //   // Pong received
      //   break;
      // case 'subscriptionResponse':
      //   console.log('Subscription response:', message.data);
      // break;
      default:
        console.log('Unknown channel:', message.channel);
        console.log(message);
    }
  }

  private handleAssetData(data: ActiveAssetData) {
    useHyperliquidStore.getState().updateAssetData(data);
  }

  private handleAllMids(data: AllMids) {
    if (data.mids) {
      useHyperliquidStore.getState().updateMids(data.mids);
    }
  }

  private handleOrderBook(data: WsBook) {
    useHyperliquidStore.getState().updateOrderBook(data);
  }

  private handleBlock(data: NewBlock) {
    // Add to queue for throttling
    this.blockQueue.push(data);
    this.throttleBlockUpdates();
  }

  private throttleBlockUpdates() {
    if (this.blockThrottleTimer) return;

    this.blockThrottleTimer = setTimeout(() => {
      if (this.blockQueue.length > 0) {
        // Process all queued blocks at once
        const blocksToProcess = [...this.blockQueue];
        this.blockQueue = [];

        // Add all blocks to store
        blocksToProcess.forEach(block => {
          useBlockStore.getState().addRealtimeBlock(block);
        });
      }

      this.blockThrottleTimer = null;
    }, this.THROTTLE_DELAY);
  }

  private handleTrades(data: Trade | Trade[]) {
    const trades = Array.isArray(data) ? data : [data];
    useHyperliquidStore.getState().addTrades(trades);
  }

  private handleBbo(data: WsBbo) {
    useHyperliquidStore.getState().updateBbo(data);
  }

  private handleCandles(data: Candle | Candle[]) {
    const candles = Array.isArray(data) ? data : [data];
    if (candles.length > 0) {
      useHyperliquidStore.getState().updateCandles(candles[0].i || '1m', candles);
    }
  }

  private handleAssetCtx(data: WsActiveAssetCtx) {
    useHyperliquidStore.getState().setActiveAssetCtx(data.ctx as any);
  }

  private handleOrderUpdates(data: WsOrder | WsOrder[]) {
    const orders = Array.isArray(data) ? data : [data];
    usePortfolioStore.getState().updateUserOpenOrders(orders);
    // TODO: Update order store
  }

  private handleUserEvents(data: WsUserEvent) {
    console.log('User event:', data);
    // TODO: Handle user events
  }

  private handleUserFills(data: WsUserFills) {
    if (typeof window !== 'undefined' && (window as any).playVideoForFill) {
      (window as any).playVideoForFill(data.fills[0]);
    }
  }

  private handleUserFundings(data: WsUserFundings) {
    console.log('User fundings:', data);
    // TODO: Update funding store
  }

  private handleLedgerUpdates(data: WsUserNonFundingLedgerUpdates) {
    console.log('Ledger updates:', data);
    // TODO: Update ledger
  }

  private handleNotification(data: Notification) {
    console.log('Notification:', data);
    // TODO: Show notification
  }

  private handleOrder(data: any) {
    if (!data || !data.price || !data.amount || !data.side || !data.market) {
      return;
    }

    const order: RealtimeOrder = {
      orderId: data.orderId,
      price: data.price,
      amount: data.amount,
      side: data.side,
      market: data.market,
      blockHeight: data.blockHeight,
      txHash: data.txHash,
      timestamp: Date.now(),
    };

    // Add to queue for throttling
    this.orderQueue.push(order);
    this.throttleOrderUpdates();
  }

  private handleOrderFilled(data: OrderFilled) {
    if (!data) return;

    // Add to queue for throttling
    this.orderFillQueue.push(data);
    this.throttleOrderFillUpdates();
  }

  private throttleOrderFillUpdates() {
    if (this.orderFillThrottleTimer) return;

    this.orderFillThrottleTimer = setTimeout(() => {
      if (this.orderFillQueue.length > 0) {
        // Process all queued order fills at once
        const fillsToProcess = [...this.orderFillQueue];
        this.orderFillQueue = [];

        // Add all order fills to store
        useHyperliquidStore.getState().addOrderFills(fillsToProcess);
      }

      this.orderFillThrottleTimer = null;
    }, this.THROTTLE_DELAY);
  }

  private throttleOrderUpdates() {
    if (this.orderThrottleTimer) return;

    this.orderThrottleTimer = setTimeout(() => {
      if (this.orderQueue.length > 0) {
        // Process all queued orders at once
        const ordersToProcess = [...this.orderQueue];
        this.orderQueue = [];

        // Add all orders to store
        ordersToProcess.forEach(order => {
          useOrderStore.getState().addRealtimeOrder(order);
        });
      }

      this.orderThrottleTimer = null;
    }, this.THROTTLE_DELAY);
  }

  private handleWebData2(data: WebData2) {
    useHyperliquidStore.getState().setMetaAndAssetCtxs(data.meta.universe as any, data.assetCtxs);
    if (data.clearinghouseState) {
      usePortfolioStore.getState().setPerpClearinghouseState(data.clearinghouseState as any);
    }
    if (data.openOrders) {
      usePortfolioStore.getState().setUserOpenOrders(data.openOrders as any);
    }
    if (data.spotState?.balances) {
      usePortfolioStore.getState().setSpotClearinghouseBalances(data.spotState.balances as any);
    }
  }

  // ============ Subscription Management ============
  subscribe(subscription: any) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // Store subscription for reconnect
      const key = this.getSubscriptionKey(subscription);
      this.subscriptions.set(key, subscription);
      return;
    }

    const message = {
      method: 'subscribe',
      subscription,
    };

    this.ws.send(JSON.stringify(message));

    const key = this.getSubscriptionKey(subscription);
    this.subscriptions.set(key, subscription);
    console.log('📡 Subscribed:', subscription);
  }

  unsubscribe(subscription: any) {
    const key = this.getSubscriptionKey(subscription);

    if (this.ws?.readyState === WebSocket.OPEN) {
      const message = {
        method: 'unsubscribe',
        subscription,
      };
      this.ws.send(JSON.stringify(message));
    }

    this.subscriptions.delete(key);
    console.log('📡 Unsubscribed:', subscription);
  }

  private resubscribeAll() {
    console.log('🔄 Resubscribing all...');
    this.subscriptions.forEach(subscription => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        const message = {
          method: 'subscribe',
          subscription,
        };
        this.ws.send(JSON.stringify(message));
      }
    });
  }

  private getSubscriptionKey(subscription: any): string {
    return `${subscription.type}_${JSON.stringify(subscription)}`;
  }

  // ============ High-level Subscription Methods ============
  subscribeToMarket(coin: string) {
    console.log(`📈 Subscribing to market: ${coin}`);

    // Subscribe to new market
    // this.subscribe({ type: 'l2Book', coin });
    // this.subscribe({ type: 'trades', coin });
    // this.subscribe({ type: 'bbo', coin });
    // this.subscribe({ type: 'activeAssetCtx', coin });
    // this.subscribe({ type: 'candle', coin, interval: '1m' });
    this.subscribe({ type: 'order', coin });
  }

  unsubscribeFromMarket(coin: string) {
    console.log(`📈 Unsubscribing from market: ${coin}`);

    this.unsubscribe({ type: 'l2Book', coin });
    this.unsubscribe({ type: 'order', coin });
    // this.unsubscribe({ type: 'trades', coin });
    // this.unsubscribe({ type: 'bbo', coin });
    // this.unsubscribe({ type: 'activeAssetCtx', coin });
    // this.unsubscribe({ type: 'candle', coin, interval: '1m' });
  }

  subscribeToUser(user: string) {
    console.log(`👤 Subscribing to user: ${user}`);

    // this.subscribe({ type: 'orderUpdates', user });
    // this.subscribe({ type: 'userEvents', user });
    // this.subscribe({ type: 'userFills', user });
    // this.subscribe({ type: 'userFundings', user });
    // this.subscribe({ type: 'userNonFundingLedgerUpdates', user });

    // this.subscribe({ type: 'webData2', user });
  }

  unsubscribeFromUser(user: string) {
    console.log(`👤 Unsubscribing from user: ${user}`);

    // this.unsubscribe({ type: 'orderUpdates', user });
    // this.unsubscribe({ type: 'userEvents', user });
    // this.unsubscribe({ type: 'userFills', user });
    // this.unsubscribe({ type: 'userFundings', user });
    // this.unsubscribe({ type: 'userNonFundingLedgerUpdates', user });
    // this.unsubscribe({ type: 'webData2', user });
  }

  // subscribeToUserCoin(coin: string, user: Address) {
  //   this.subscribe({ type: 'activeAssetData', coin, user });
  // }

  unsubscribeFromUserCoin(coin: string, user: Address) {
    this.unsubscribe({ type: 'activeAssetData', coin, user });
  }

  subscribeToGlobal() {
    console.log('🌍 Subscribing to global data');
    this.subscribe({ type: 'allMids' });
  }

  unsubscribeFromGlobal() {
    console.log('🌍 Unsubscribing from global data');
    this.unsubscribe({ type: 'allMids' });
  }

  // ============ Connection Status ============
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  onConnectionChange(callback: (isConnected: boolean) => void) {
    this.connectionListeners.add(callback);
    // Return unsubscribe function
    return () => {
      this.connectionListeners.delete(callback);
    };
  }

  private notifyConnectionChange(isConnected: boolean) {
    this.connectionListeners.forEach(callback => callback(isConnected));
  }

  // ============ Initial Setup ============
  initializeDefaultSubscriptions() {
    // Subscribe to global data
    // this.subscribeToGlobal();
    // Default market subscription is handled by DataLayout based on selectedAssetNumber
  }

  // ============ Cleanup ============
  disconnect() {
    console.log('🔌 Disconnecting WebSocket...');

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Clear throttling timers
    if (this.orderThrottleTimer) {
      clearTimeout(this.orderThrottleTimer);
      this.orderThrottleTimer = null;
    }

    if (this.blockThrottleTimer) {
      clearTimeout(this.blockThrottleTimer);
      this.blockThrottleTimer = null;
    }

    if (this.orderFillThrottleTimer) {
      clearTimeout(this.orderFillThrottleTimer);
      this.orderFillThrottleTimer = null;
    }

    // Clear queues
    this.orderQueue = [];
    this.blockQueue = [];
    this.orderFillQueue = [];

    this.stopPing();
    this.subscriptions.clear();
    this.connectionListeners.clear();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}

// Export singleton instance
export const wsManager = WebSocketManager.getInstance();
